import { ChevronDown, FileUp, Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { Measurement } from '@coachapp/shared';
import { FormPageLayout } from '../components/layout/FormPageLayout';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import {
  useCreateMeasurementMutation,
  useGenerateInsightMutation,
  useGetClientQuery,
  useGetMeasurementsQuery,
  useUpdateMeasurementMutation,
} from '../store/coachApi';
import { extractTextFromPdf } from '../utils/pdfParser';

interface MeasurementFormContentProps {
  clientId: string;
  clientName?: string;
  initialData?: Measurement;
  onClose: () => void;
}

const MeasurementFormContent = ({
  clientId,
  clientName,
  initialData,
  onClose,
}: MeasurementFormContentProps) => {
  const { t } = useTranslation();
  const [createMeasurement, { isLoading: creating }] = useCreateMeasurementMutation();
  const [updateMeasurement, { isLoading: updating }] = useUpdateMeasurementMutation();
  const [generateInsightMutation, { isLoading: generatingInsight }] = useGenerateInsightMutation();
  const loading = creating || updating;
  const [parsingPdf, setParsingPdf] = useState(false);
  const [showAiMenu, setShowAiMenu] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    weight: initialData?.weight || '',
    height: initialData?.height || '',
    age: initialData?.age || '',
    analysesText: initialData?.analysesText || '',
    analysesRaw: initialData?.analysesRaw || '',
    notes: initialData?.notes || '',
    trainerComment: initialData?.trainerComment || '',
  });

  const generateInsight = async (role: string) => {
    setShowAiMenu(false);
    setError('');
    try {
      const res = await generateInsightMutation({
        role,
        data: formData,
      }).unwrap();

      const roleName = {
        COACH: t('Коуч'),
        DOCTOR: t('Врач'),
        NUTRITIONIST: t('Нутрициолог'),
      }[role as 'COACH' | 'DOCTOR' | 'NUTRITIONIST'];

      setFormData((prev) => {
        const notes = prev.notes.trim();
        const separator = notes ? '\n\n' : '';
        const header = `--- AI ${roleName} ---\n`;
        return {
          ...prev,
          notes: header + res.content + separator + notes,
        };
      });
    } catch (err) {
      setError(
        err &&
          typeof err === 'object' &&
          'message' in err &&
          typeof err.message === 'string'
          ? err.message
          : getApiErrorMessage(
              err,
              'ИИ сейчас недоступен.',
            ),
      );
    }
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError(t('Пожалуйста, выберите PDF файл'));
      return;
    }

    setParsingPdf(true);
    setError('');
    try {
      const text = await extractTextFromPdf(file);
      setFormData((prev) => ({ ...prev, analysesRaw: text }));
    } catch (err) {
      console.error('PDF parsing failed:', err);
      setError(
        t(
          'Не удалось извлечь текст из PDF. Пожалуйста, введите данные вручную.',
        ),
      );
    } finally {
      setParsingPdf(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (initialData) {
        await updateMeasurement({
          id: initialData.id,
          clientId,
          data: formData,
        }).unwrap();
      } else {
        await createMeasurement({ clientId, data: formData }).unwrap();
      }
      onClose();
    } catch (err) {
      setError(
        err &&
          typeof err === 'object' &&
          'message' in err &&
          typeof err.message === 'string'
          ? err.message
          : getApiErrorMessage(err, 'Ошибка при сохранении'),
      );
    }
  };

  const pageTitle = initialData ? t('Редактировать замер') : t('Новый замер');

  return (
    <FormPageLayout title={pageTitle} subtitle={clientName} onBack={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-600 rounded-2xl text-center text-sm font-medium">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="section-label ml-1">
              {t('Вес (кг)')}
            </label>
            <input
              type="text"
              className="input-field font-medium"
              value={formData.weight}
              onChange={(e) =>
                setFormData({ ...formData, weight: e.target.value })
              }
              placeholder="75.5"
            />
          </div>
          <div className="space-y-1.5">
            <label className="section-label ml-1">
              {t('Рост (см)')}
            </label>
            <input
              type="text"
              className="input-field font-medium"
              value={formData.height}
              onChange={(e) =>
                setFormData({ ...formData, height: e.target.value })
              }
              placeholder="180"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="section-label ml-1">
            {t('Возраст (лет)')}
          </label>
          <input
            type="text"
            className="input-field font-medium"
            value={formData.age}
            onChange={(e) =>
              setFormData({ ...formData, age: e.target.value })
            }
            placeholder="32"
          />
        </div>

        <div className="space-y-3">
          <label className="section-label ml-1">
            {t('Анализы')}
          </label>

          <div className="relative group">
            <input
              type="file"
              accept=".pdf"
              className="hidden"
              id="pdf-upload"
              onChange={handlePdfUpload}
              disabled={parsingPdf}
            />
            <label
              htmlFor="pdf-upload"
              className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all text-sm font-bold text-slate-500
                  ${parsingPdf ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {parsingPdf ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <FileUp size={20} />
              )}
              {parsingPdf ? t('Парсинг PDF...') : t('Загрузить PDF анализы')}
            </label>
          </div>

          <textarea
            className="input-field min-h-[100px] resize-y"
            value={formData.analysesText}
            onChange={(e) =>
              setFormData({ ...formData, analysesText: e.target.value })
            }
            placeholder={t('Ваш комментарий к анализам...')}
          />

          {formData.analysesRaw && (
            <div className="p-3 bg-emerald-50 rounded-2xl">
              <p className="micro-label text-emerald-600 mb-1">
                {t('Текст извлечен из PDF')}
              </p>
              <div className="text-xs text-emerald-700/80 truncate">
                {formData.analysesRaw}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="section-label ml-1">
            {t('Комментарий для клиента')}
          </label>
          <p className="text-xs text-slate-500 ml-1 mb-1">
            {t('Виден клиенту в разделе «Прогресс»')}
          </p>
          <textarea
            className="input-field min-h-[100px] resize-y"
            value={formData.trainerComment}
            onChange={(e) =>
              setFormData({ ...formData, trainerComment: e.target.value })
            }
            placeholder={t('Напишите комментарий к этому замеру')}
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between items-center mb-1">
            <label className="section-label ml-1">
              {t('Дополнительно')}
            </label>

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAiMenu(!showAiMenu)}
                disabled={generatingInsight}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-soft hover:bg-primary/15 text-primary rounded-xl text-xs font-semibold transition-colors disabled:opacity-50"
              >
                {generatingInsight ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    <span>{t('Генерация...')}</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={12} />
                    <span>{t('AI Помощник')}</span>
                    <ChevronDown
                      size={10}
                      className={`transition-transform ${showAiMenu ? 'rotate-180' : ''}`}
                    />
                  </>
                )}
              </button>

              {showAiMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-app-border rounded-2xl shadow-card z-10 py-2">
                  <button
                    type="button"
                    onClick={() => generateInsight('COACH')}
                    className="w-full text-left px-4 py-2.5 text-[11px] font-bold text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-2"
                  >
                    <span>🏃‍♂️</span> {t('Интерпретировать как Тренер')}
                  </button>
                  <button
                    type="button"
                    onClick={() => generateInsight('DOCTOR')}
                    className="w-full text-left px-4 py-2.5 text-[11px] font-bold text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-2"
                  >
                    <span>🩺</span> {t('Анализировать как Врач')}
                  </button>
                  <button
                    type="button"
                    onClick={() => generateInsight('NUTRITIONIST')}
                    className="w-full text-left px-4 py-2.5 text-[11px] font-bold text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-2"
                  >
                    <span>🍎</span> {t('Советы Нутрициолога')}
                  </button>
                </div>
              )}
            </div>
          </div>

          <textarea
            className="input-field min-h-[120px] resize-y"
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            placeholder={t('Общая информация о состоянии...')}
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <button type="button" className="btn-secondary flex-1 min-h-12" onClick={onClose}>
            {t('Отмена')}
          </button>
          <button
            type="submit"
            className="btn-primary flex-1 sm:flex-[2] min-h-12"
            disabled={loading || parsingPdf || generatingInsight}
          >
            {loading
              ? t('Сохранение...')
              : initialData
                ? t('Сохранить изменения')
                : t('Сохранить замер')}
          </button>
        </div>
      </form>
    </FormPageLayout>
  );
};

const MeasurementFormRoute = () => {
  const navigate = useTransitionNavigate();
  const { id: clientId, measurementId } = useParams();
  const { t } = useTranslation();
  const { data: client } = useGetClientQuery({ id: clientId! }, { skip: !clientId });
  const { data: measurements, isLoading } = useGetMeasurementsQuery(
    { clientId: clientId! },
    { skip: !clientId || !measurementId },
  );
  const initialData = measurementId
    ? measurements?.find((item) => item.id === measurementId)
    : undefined;
  const detailLoading = !!measurementId && isLoading;
  const close = () => navigate(-1);
  const pageTitle = initialData ? t('Редактировать замер') : t('Новый замер');

  if (!clientId) {
    return null;
  }

  if (detailLoading) {
    return (
      <FormPageLayout title={pageTitle} subtitle={client?.name} onBack={close} loading />
    );
  }

  return (
    <MeasurementFormContent
      key={initialData?.id ?? 'new'}
      clientId={clientId}
      clientName={client?.name}
      initialData={initialData}
      onClose={close}
    />
  );
};

export default MeasurementFormRoute;
